import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PointsListView, PointsMapView, RouteVariator } from 'bricks';
import { TAKEAWAY_VIEW_LIST, TAKEAWAY_VIEW_MAP } from 'store/ProductDetails/constants';
import { isScreenBelow800, isScreenBelow1004 } from 'store/common/settings/breakpoint/selectors';
import {
  getDeliveryOfficesList,
  getDeliveryFilteredOfficesCount,
  getDeliveryPickupBuyViewState,
  getDeliveryTakeawayPins,
} from 'store/ProductDetails/selectors';
import { getDeliveryShowPointsCount } from 'store/ProductDetails/delivery/selectors';
import { setPointsToRenderLength, hidePointsToRender } from 'store/ProductDetails/delivery/actions';
import { togglePickupBuy, addToBasket } from 'store/ProductDetails/common/actions';
// import { ProductParameter } from '@beef/legacy-ui-kit';

/**
 * Slug based switch for takeaway map/list views
 */
function PointsViewSwitch({
  slug,
  pins,
  points,
  pointsCount,
  pointsToRenderLength,
  viewState,
  isMobile,
  isTablet,
  openPickupBuy,
  goBasket,
  showMorePoints,
  idForMap,
  classForMap,
  classForList,
  hidePoints,
}) {
  switch (slug) {
    case TAKEAWAY_VIEW_MAP:
      return (
        <PointsMapView
          idForMap={idForMap}
          classForMap={classForMap}
          {...{
            idForMap,
            classForMap,
            points,
            pins,
            viewState,
            isMobile,
            isTablet,
            openPickupBuy,
            goBasket,
          }}
        />
      );
    case TAKEAWAY_VIEW_LIST:
    default:
      return (
        <PointsListView
          className={classForList}
          {...{
            points,
            pointsCount,
            pointsToRenderLength,
            viewState,
            isMobile,
            isTablet,
            openPickupBuy,
            goBasket,
            showMorePoints,
            hidePoints,
          }}
        />
      );
  }
}

PointsViewSwitch.propTypes = {
  slug: PropTypes.string,
  idForMap: PropTypes.string,
  classForMap: PropTypes.string,
  classForList: PropTypes.string,
  noRoute: PropTypes.bool,
  // props for map view and list view
  points: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
      opening: PropTypes.string,
      priceTitle: PropTypes.string,
      priceSubTitle: PropTypes.string,
      availableDate: PropTypes.string,
      onlyCard: PropTypes.bool,
      onlyCardTitle: PropTypes.string,
      subway: PropTypes.shape({
        name: PropTypes.string,
        color: PropTypes.string,
      }),
    }),
  ),
  pins: PropTypes.object,
  pointsCount: PropTypes.number,
  pointsToRenderLength: PropTypes.number,
  showMorePoints: PropTypes.func,
  hidePoints: PropTypes.func,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  openPickupBuy: PropTypes.func,
  goBasket: PropTypes.func,
  viewState: PropTypes.string,
  activeSlug: PropTypes.string, // invariant to routing compare
};

/**
 * url to slug router for takeaway views
 */
function TakeawayPopupRouter({
  tabs,
  pins,
  points,
  pointsCount,
  pointsToRenderLength,
  viewState,
  isMobile,
  isTablet,
  openPickupBuy,
  goBasket,
  showMorePoints,
  idForMap,
  classForMap,
  classForList,
  noRoute,
  hidePoints,
}) {
  const redirectIndex = tabs.length === 1 ? 0 : 1;
  return (
    <RouteVariator {...{ noRoute }} redirectUrl={tabs[redirectIndex].url}>
      {tabs.map(({ slug, url }) => (
        <PointsViewSwitch
          path={url}
          key={slug}
          {...{
            slug,
            idForMap,
            classForMap,
            classForList,
            points,
            pins,
            pointsCount,
            pointsToRenderLength,
            viewState,
            isMobile,
            isTablet,
            openPickupBuy,
            goBasket,
            showMorePoints,
            hidePoints,
          }}
        />
      ))}
    </RouteVariator>
  );
}

TakeawayPopupRouter.defaultProps = {
  noRoute: false,
};

TakeawayPopupRouter.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  idForMap: PropTypes.string,
  classForMap: PropTypes.string,
  classForList: PropTypes.string,
  noRoute: PropTypes.bool,
  // props for map view and list view
  points: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
      opening: PropTypes.string,
      priceTitle: PropTypes.string,
      priceSubTitle: PropTypes.string,
      availableDate: PropTypes.string,
      onlyCard: PropTypes.bool,
      onlyCardTitle: PropTypes.string,
      subway: PropTypes.shape({
        name: PropTypes.string,
        color: PropTypes.string,
      }),
    }),
  ),
  pins: PropTypes.object,
  pointsCount: PropTypes.number,
  pointsToRenderLength: PropTypes.number,
  showMorePoints: PropTypes.func,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  openPickupBuy: PropTypes.func,
  goBasket: PropTypes.func,
  hidePoints: PropTypes.func,
  viewState: PropTypes.string,
  setProductDetailsOffices: PropTypes.func,
};

const mapStateToProps = (state) => ({
  pins: getDeliveryTakeawayPins(state),
  points: getDeliveryOfficesList(state),
  pointsCount: getDeliveryFilteredOfficesCount(state),
  pointsToRenderLength: getDeliveryShowPointsCount(state),
  viewState: getDeliveryPickupBuyViewState(state),
  isMobile: isScreenBelow800(state),
  isTablet: isScreenBelow1004(state),
});

const mapDispatchToProps = {
  openPickupBuy: togglePickupBuy,
  goBasket: addToBasket,
  showMorePoints: setPointsToRenderLength,
  hidePoints: hidePointsToRender,
};

export default connect(mapStateToProps, mapDispatchToProps)(TakeawayPopupRouter);
